import React from "react";
import { useTranslation } from "react-i18next";
import { ScreenContainer } from "@/shared/ui/ScreenContainer";
import useViewerPermissions from "@/features/workspace/hooks/useViewerPermissions";
import { AccessLevel } from "@/shared/constants";
import { ResourceEnum } from "@/shared/constants/resources";

interface RequireScorecardsAccessProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * The scorecard library is workspace ADMIN/OWNER territory (account EDIT/FULL).
 * Members only use scorecards through the projects they collaborate on, so all
 * /scorecards routes are locked for them. UX only — the API enforces this in
 * controllers/scorecard (see api/docs/PERMISSIONS_SYSTEM.md, Scorecard Management).
 */
export function RequireScorecardsAccess({
  children,
  className = "overflow-y-auto",
}: RequireScorecardsAccessProps) {
  const { t } = useTranslation();
  const { validateAccessToResource, permissionsLoading } =
    useViewerPermissions();

  const hasAccess = validateAccessToResource({
    resourceType: ResourceEnum.account,
    accessLevel: AccessLevel.edit,
  });

  if (permissionsLoading)
    return (
      <ScreenContainer
        loading
        loadingMessage={t("common.checking_access")}
        className={className}
      />
    );
  if (!hasAccess)
    return <ScreenContainer permissionLocked className={className} />;

  return <>{children}</>;
}
